import anime from 'animejs';
import Swal from 'sweetalert2';
import { SystemController } from './system_controller.js';

document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded, initializing app...');
  
  const app = {
    init() {
      this.setupEventListeners();
      this.loadSettings();
      this.updateUI();
      this.initializeAnimations();
    },

    state: {
      currentLanguage: 'hi',
      isDarkMode: false,
      isListening: false,
      recognition: null,
      responses: {
        en: {
          greeting: [
            "Hello! How can I help you today?",
            "Hi there! What can I do for you?",
            "Greetings! How may I assist you?",
            "Welcome! What brings you here today?"
          ],
          farewell: [
            "Goodbye! Have a great day!",
            "See you later!",
            "Take care!",
            "Until next time!"
          ],
          thanks: [
            "You're welcome!",
            "Happy to help!",
            "My pleasure!",
            "Anytime!"
          ],
          unknown: [
            "I'm not sure I understand. Could you rephrase that?",
            "I'm still learning. Could you try asking in a different way?",
            "I didn't quite catch that. Could you explain more?"
          ]
        },
        hi: {
          greeting: [
            "नमस्ते! मैं आपकी क्या मदद कर सकता हूँ?",
            "नमस्कार! मैं आपके लिए क्या कर सकता हूँ?",
            "प्रणाम! मैं आपकी कैसे सहायता कर सकता हूँ?"
          ],
          farewell: [
            "अलविदा! अच्छा दिन हो!",
            "फिर मिलेंगे!",
            "ख्याल रखना!"
          ],
          thanks: [
            "आपका स्वागत है!",
            "मदद करके खुशी हुई!",
            "मेरा सौभाग्य!"
          ],
          unknown: [
            "मुझे समझ नहीं आया। क्या आप दोबारा कह सकते हैं?",
            "मैं अभी सीख रहा हूं। क्या आप किसी और तरीके से पूछ सकते हैं?"
          ]
        }
      }
    },

    elements: {
      orbCore: document.getElementById('orbCore'),
      orbStatus: document.getElementById('orbStatus'),
      waveAnimation: document.getElementById('waveAnimation'),
      inputField: document.getElementById('inputField'),
      sendBtn: document.getElementById('sendBtn'),
      voiceBtn: document.getElementById('voiceBtn'),
      stopBtn: document.getElementById('stopBtn'),
      darkModeToggle: document.getElementById('darkModeToggle'),
      messagesContainer: document.getElementById('messagesContainer'),
      languageOptions: document.querySelectorAll('.lang-option')
    },

    setupEventListeners() {
      console.log('Setting up event listeners...');
      
      this.elements.inputField.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.handleSendMessage();
        }
      });

      this.elements.sendBtn.addEventListener('click', () => this.handleSendMessage());
      this.elements.voiceBtn.addEventListener('click', () => this.toggleVoiceRecognition());
      this.elements.stopBtn.addEventListener('click', () => this.stopAll());
      this.elements.darkModeToggle.addEventListener('change', () => this.toggleDarkMode());

      this.elements.languageOptions.forEach(option => {
        option.addEventListener('click', () => this.handleLanguageChange(option.dataset.lang));
      });

      // Quick action buttons
      document.querySelectorAll('.quick-actions .action-btn').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleQuickAction(e.currentTarget.dataset.action));
      });

      // Feature buttons
      document.getElementById('locationBtn').addEventListener('click', () => this.handleLocation());
      document.getElementById('calculatorBtn').addEventListener('click', () => this.handleCalculator());
      document.getElementById('reminderBtn').addEventListener('click', () => this.handleReminder());
      document.getElementById('settingsBtn').addEventListener('click', () => this.handleSettings());

      console.log('Event listeners setup complete');
    },

    async handleSendMessage() {
      const message = this.elements.inputField.textContent.trim();
      if (!message) return;

      this.addMessage(message, 'user-message');
      this.elements.inputField.textContent = '';

      const typingIndicator = this.addMessage('DELTA is typing...', 'assistant-message');
      
      try {
        const response = await this.generateResponse(message);
        typingIndicator.remove();
        this.addMessage(response, 'assistant-message');
        this.speak(response);
      } catch (error) {
        console.error('Error generating response:', error);
        typingIndicator.remove();
        this.addMessage(this.getRandomResponse('unknown'), 'assistant-message');
      }

      this.scrollToBottom();
    },

    async generateResponse(message) {
      const lowercaseMessage = message.toLowerCase();
      
      // Handle application opening commands
      if (lowercaseMessage.includes('open') || lowercaseMessage.includes('ओपन')) {
        const appName = this.extractAppName(lowercaseMessage);
        if (appName) {
          return this.handleAppOpening(appName);
        }
      }
      
      // Handle greetings
      if (this.isGreeting(lowercaseMessage)) {
        return this.getRandomResponse('greeting');
      }
      
      // Handle farewells
      if (this.isFarewell(lowercaseMessage)) {
        return this.getRandomResponse('farewell');
      }
      
      // Handle thanks
      if (this.isThanks(lowercaseMessage)) {
        return this.getRandomResponse('thanks');
      }

      // Handle weather queries
      if (this.isWeatherQuery(lowercaseMessage)) {
        return await this.handleWeatherQuery(message);
      }

      // Handle time queries
      if (this.isTimeQuery(lowercaseMessage)) {
        return this.handleTimeQuery();
      }

      // Handle calculator queries
      if (this.isCalculatorQuery(lowercaseMessage)) {
        return this.handleCalculatorQuery(message);
      }

      // Handle translation requests
      if (this.isTranslationRequest(lowercaseMessage)) {
        return this.handleTranslationRequest(message);
      }

      // Handle general knowledge queries
      return this.handleGeneralQuery(message);
    },

    isGreeting(message) {
      const greetings = {
        en: ['hello', 'hi', 'hey', 'good morning', 'good afternoon', 'good evening'],
        hi: ['नमस्ते', 'नमस्कार', 'प्रणाम', 'सुप्रभात']
      };
      return greetings[this.state.currentLanguage].some(greeting => message.includes(greeting));
    },

    isFarewell(message) {
      const farewells = {
        en: ['bye', 'goodbye', 'see you', 'farewell'],
        hi: ['अलविदा', 'फिर मिलेंगे', 'नमस्कार']
      };
      return farewells[this.state.currentLanguage].some(farewell => message.includes(farewell));
    },

    isThanks(message) {
      const thanks = {
        en: ['thank', 'thanks', 'appreciate'],
        hi: ['धन्यवाद', 'शुक्रिया']
      };
      return thanks[this.state.currentLanguage].some(thank => message.includes(thank));
    },

    isWeatherQuery(message) {
      const weatherTerms = {
        en: ['weather', 'temperature', 'forecast'],
        hi: ['मौसम', 'तापमान']
      };
      return weatherTerms[this.state.currentLanguage].some(term => message.includes(term));
    },

    isTimeQuery(message) {
      const timeTerms = {
        en: ['time', 'what time', 'current time'],
        hi: ['समय', 'वक्त']
      };
      return timeTerms[this.state.currentLanguage].some(term => message.includes(term));
    },

    isCalculatorQuery(message) {
      return /[0-9+\-*\/()]/g.test(message);
    },

    isTranslationRequest(message) {
      const translationTerms = {
        en: ['translate', 'translation', 'meaning'],
        hi: ['अनुवाद', 'मतलब']
      };
      return translationTerms[this.state.currentLanguage].some(term => message.includes(term));
    },

    getRandomResponse(type) {
      const responseList = this.state.responses[this.state.currentLanguage][type];
      return responseList[Math.floor(Math.random() * responseList.length)];
    },

    async handleGeneralQuery(message) {
      // Here you would typically make an API call to a language model
      // For now, we'll return a more helpful response
      const responses = [
        `I understand you're asking about "${message}". Let me help you with that.`,
        `That's an interesting question about ${message}. Here's what I know...`,
        `Regarding ${message}, I can tell you that...`,
        `Let me explain about ${message}...`
      ];
      return responses[Math.floor(Math.random() * responses.length)];
    },

    async handleWeatherQuery(message) {
      // Extract location from message (simplified)
      const location = message.replace(/weather|temperature|forecast|मौसम|तापमान/gi, '').trim();
      return `I would show you the weather for ${location}, but I need to be connected to a weather API first.`;
    },

    handleTimeQuery() {
      const now = new Date();
      return this.state.currentLanguage === 'hi' 
        ? `वर्तमान समय ${now.toLocaleTimeString('hi-IN')} है।`
        : `The current time is ${now.toLocaleTimeString()}.`;
    },

    handleCalculatorQuery(message) {
      try {
        // Remove all characters except numbers and basic operators
        const expression = message.replace(/[^0-9+\-*\/()]/g, '');
        const result = eval(expression);
        return `${expression} = ${result}`;
      } catch (error) {
        return this.getRandomResponse('unknown');
      }
    },

    async handleTranslationRequest(message) {
      // Simplified translation handling
      return "I would translate this for you, but I need to be connected to a translation API first.";
    },

    addMessage(content, type) {
      const messageElement = document.createElement('div');
      messageElement.className = `message ${type}`;
      messageElement.textContent = content;
      this.elements.messagesContainer.appendChild(messageElement);
      return messageElement;
    },

    scrollToBottom() {
      this.elements.messagesContainer.scrollTop = this.elements.messagesContainer.scrollHeight;
    },

    handleQuickAction(action) {
      switch (action) {
        case 'search':
          this.elements.inputField.textContent = 'search: ';
          this.elements.inputField.focus();
          break;
        case 'translate':
          this.elements.inputField.textContent = 'translate: ';
          this.elements.inputField.focus();
          break;
        case 'weather':
          this.elements.inputField.textContent = 'weather in ';
          this.elements.inputField.focus();
          break;
        case 'news':
          this.handleNewsRequest();
          break;
      }
    },

    handleLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const message = `Your location: ${position.coords.latitude}, ${position.coords.longitude}`;
            this.addMessage(message, 'assistant-message');
          },
          error => {
            this.addMessage('Unable to get location: ' + error.message, 'assistant-message');
          }
        );
      } else {
        this.addMessage('Geolocation is not supported by your browser.', 'assistant-message');
      }
    },

    handleCalculator() {
      this.elements.inputField.textContent = 'calculate: ';
      this.elements.inputField.focus();
    },

    handleReminder() {
      this.addMessage('What would you like me to remind you about?', 'assistant-message');
      this.elements.inputField.textContent = 'remind me to ';
      this.elements.inputField.focus();
    },

    handleSettings() {
      Swal.fire({
        title: 'Settings',
        html: `
          <div class="settings-container">
            <div class="setting-item">
              <label>Voice Speed</label>
              <input type="range" id="voiceSpeed" min="0.5" max="2" step="0.1" value="1">
            </div>
            <div class="setting-item">
              <label>Voice Pitch</label>
              <input type="range" id="voicePitch" min="0.5" max="2" step="0.1" value="1">
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Save',
        cancelButtonText: 'Cancel'
      });
    },

    async handleNewsRequest() {
      this.addMessage('Here are the latest headlines...', 'assistant-message');
      // Here you would typically fetch news from an API
      this.addMessage('News API integration coming soon!', 'assistant-message');
    },

    toggleVoiceRecognition() {
      if (this.state.isListening) {
        this.stopVoiceRecognition();
      } else {
        this.startVoiceRecognition();
      }
    },

    startVoiceRecognition() {
      if (!('webkitSpeechRecognition' in window)) {
        Swal.fire({
          title: 'Error',
          text: 'Speech recognition is not supported in your browser.',
          icon: 'error'
        });
        return;
      }

      this.state.recognition = new webkitSpeechRecognition();
      this.state.recognition.continuous = false;
      this.state.recognition.interimResults = false;
      this.state.recognition.lang = this.state.currentLanguage === 'hi' ? 'hi-IN' : 'en-US';

      this.state.recognition.onstart = () => {
        this.state.isListening = true;
        this.elements.orbCore.classList.add('listening');
        this.elements.orbStatus.textContent = 'Listening...';
        this.elements.orbStatus.style.opacity = '1';
      };

      this.state.recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        this.elements.inputField.textContent = transcript;
        this.handleSendMessage();
      };

      this.state.recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        this.stopVoiceRecognition();
      };

      this.state.recognition.onend = () => {
        this.stopVoiceRecognition();
      };

      this.state.recognition.start();
    },

    stopVoiceRecognition() {
      if (this.state.recognition) {
        this.state.recognition.stop();
      }
      this.state.isListening = false;
      this.elements.orbCore.classList.remove('listening');
      this.elements.orbStatus.style.opacity = '0';
    },

    speak(text) {
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = this.state.currentLanguage === 'hi' ? 'hi-IN' : 'en-US';
        window.speechSynthesis.speak(utterance);
      }
    },

    stopAll() {
      this.stopVoiceRecognition();
      window.speechSynthesis.cancel();
    },

    toggleDarkMode() {
      this.state.isDarkMode = this.elements.darkModeToggle.checked;
      this.updateUI();
      this.saveSettings();
    },

    handleLanguageChange(language) {
      this.state.currentLanguage = language;
      this.updateUI();
      this.saveSettings();
    },

    loadSettings() {
      const settings = localStorage.getItem('deltaSettings');
      if (settings) {
        const { isDarkMode, language } = JSON.parse(settings);
        this.state.isDarkMode = isDarkMode;
        this.state.currentLanguage = language || 'hi';
      }
    },

    updateUI() {
      document.body.classList.toggle('dark-mode', this.state.isDarkMode);
      this.elements.darkModeToggle.checked = this.state.isDarkMode;
      
      this.elements.languageOptions.forEach(option => {
        option.classList.toggle('active', option.dataset.lang === this.state.currentLanguage);
      });
    },

    initializeAnimations() {
      anime({
        targets: '.orb-ring',
        scale: [1, 1.1],
        opacity: [0.5, 0.2],
        easing: 'easeInOutSine',
        duration: 1500,
        loop: true,
        direction: 'alternate'
      });
    },

    saveSettings() {
      localStorage.setItem('deltaSettings', JSON.stringify({
        isDarkMode: this.state.isDarkMode,
        language: this.state.currentLanguage
      }));
    },

    extractAppName(message) {
      const appKeywords = {
        'notepad': ['notepad', 'text editor', 'text file'],
        'calculator': ['calculator', 'calc', 'math'],
        'word': ['word', 'document', 'doc'],
        'paint': ['paint', 'drawing', 'draw'],
        'terminal': ['terminal', 'command prompt', 'cmd'],
        'store': ['store', 'microsoft store', 'app store']
      };

      const lowercaseMessage = message.toLowerCase();
      
      for (const [app, keywords] of Object.entries(appKeywords)) {
        if (keywords.some(keyword => lowercaseMessage.includes(keyword))) {
          return app;
        }
      }
      
      return null;
    },

    async handleAppOpening(appName) {
      try {
        const response = await fetch('/.netlify/functions/open-app', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ appName }),
        });

        const data = await response.json();
        
        if (data.success) {
          return `Opening ${appName}...`;
        } else {
          throw new Error(data.message);
        }
      } catch (error) {
        console.error('Error opening application:', error);
        return `Sorry, I couldn't open ${appName}. ${error.message}`;
      }
    }
  };

  // Initialize the app
  app.init();
});